<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
// <altcha-widget> is registered once, in `main.ts` — see the note there.
import { useSession } from '@/state/session';
import { ApiError, api } from '@/net/api';
import BrandLogo from './BrandLogo.vue';
import PasswordField from './PasswordField.vue';
import LegalOverlay from '../landing/LegalOverlay.vue';
import { canSubmitJoin, captchaCapable, type JoinGateInput } from './join-gate';

/**
 * Sign in, enlist, or ask for a reset link. One panel with a mode switch
 * rather than three routes — there is no navigation in this app, and a
 * player who mistypes their call sign should be one click from the other
 * form, not one page load.
 *
 * Since the template port, sign-in is by **email** — the account system is
 * t-game-core's, which has no call-sign login — and enlisting requires an
 * email rather than merely inviting one. The call sign survives as the name
 * worn in battle, posted as the account's username.
 */
const session = useSession();

/**
 * `back` returns to the landing page.
 *
 * The sign-in panel is reached *from* that page now, so it needs a way out — and
 * it has to be an explicit control rather than the browser's back button, which
 * would leave the app entirely: there is no router here, so the two views are
 * `App.vue`'s own state and the history stack knows nothing about them.
 */
const emit = defineEmits<{ back: [] }>();

/**
 * The backdrop — a still (always shown, the honest fallback for every
 * failure mode: slow network, a 404'd video, a browser that blocks
 * autoplay, `prefers-reduced-motion`) plus a video, layered exactly the way
 * `LandingScreen.vue`'s "Ground" section layers its own: the still painted
 * first via CSS `background-image`, the video faded in on top of it once it
 * actually has frames on screen (`@playing`, never `@canplay`).
 *
 * Two dedicated shots for this screen (`/assets/auth/backdrop*.*`) rather
 * than the loading screen's four — those are an unrelated pool meant to
 * vary between deploys; these are purpose-built for this composition (a
 * dark, empty left third for the panel to float over) and picked one of two
 * rather than one fixed shot, the same reasoning `LoadingScreen.vue` gives
 * for its own set: variety across visits without ever looking staged.
 * `webm` is listed before `mp4`, matching the Ground section's own ordering.
 */
const BACKDROPS = [
  {
    image: '/assets/auth/backdrop.webp',
    webm: '/assets/auth/backdrop.webm',
    mp4: '/assets/auth/backdrop.mp4',
  },
  {
    image: '/assets/auth/backdrop2.webp',
    webm: '/assets/auth/backdrop2.webm',
    mp4: '/assets/auth/backdrop2.mp4',
  },
];
/**
 * Picked synchronously rather than in `onMounted` — see the earlier note
 * this replaced: the video's `<source>` children resolve once, on the
 * element's first render, and do not re-read themselves if the pick changed
 * after mount.
 */
const BACKDROP = BACKDROPS[Math.floor(Math.random() * BACKDROPS.length)];

/**
 * `hidefooter`/`hidelogo` as bare HTML attributes do nothing on this build of
 * the widget — its reflected attribute list is only
 * `auto/challenge/configuration/display/language/name/theme/type/workers`,
 * and `hideFooter`/`hideLogo` are read from the `configuration` JSON instead.
 * Passed as plain attributes they were silently ignored, so the branding
 * footer has been rendering (and adding to the widget's height) the whole
 * time.
 */
const ALTCHA_CONFIG = JSON.stringify({ hideFooter: true, hideLogo: false });
const backdropVideoReady = ref(false);
const backdropReducedMotion =
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const mode = ref<'login' | 'register' | 'forgot'>('login');
const nickname = ref('');
const email = ref('');
const password = ref('');
const repeatPassword = ref('');
const agreedToTerms = ref(false);
const legalSlug = ref<string | null>(null);

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && legalSlug.value) legalSlug.value = null;
}
onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
const altchaPayload = ref('');
const altchaEl = ref<(HTMLElement & { reset?: () => void; verify?: () => void }) | null>(null);

const isRegister = computed(() => mode.value === 'register');
const isForgot = computed(() => mode.value === 'forgot');

/**
 * Read once, not per keystroke: whether the browser will lend us the Web Crypto
 * the proof-of-work needs. It cannot change for the life of the document — it is
 * a property of the origin — and a `computed` over an ambient global would
 * merely look reactive.
 */
const canCaptcha = captchaCapable();

const gate = computed<JoinGateInput>(() => ({
  register: isRegister.value,
  nickname: nickname.value,
  email: email.value,
  password: password.value,
  repeatPassword: repeatPassword.value,
  agreedToTerms: agreedToTerms.value,
  captchaSolved: altchaPayload.value !== '',
  captchaCapable: canCaptcha,
  busy: session.busy.value,
}));

const canSubmit = computed(() => canSubmitJoin(gate.value));

/**
 * The gunsight corners run neutral until a request is actually in flight,
 * then read as locked-on — green, the exact hue the in-game HUD already
 * uses for a friendly nameplate. It reverts the moment `busy` does, which
 * a rejected attempt reaches (the error message is what explains that; a
 * green sight next to it would say the opposite thing), and a successful
 * one never gets the chance to, because the screen changes under it.
 */
const isSubmitting = computed(() => (isForgot.value ? forgotBusy.value : session.busy.value));

watch(mode, () => {
  session.clearError();
  password.value = '';
  repeatPassword.value = '';
});

function onAltchaState(event: Event): void {
  const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
  altchaPayload.value = detail?.state === 'verified' ? (detail.payload ?? '') : '';
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return;

  const ok = isRegister.value
    ? await session.register({
        nickname: nickname.value.trim(),
        email: email.value.trim(),
        password: password.value,
        altcha: altchaPayload.value,
      })
    : await session.login({
        email: email.value.trim(),
        password: password.value,
        altcha: altchaPayload.value,
      });

  // A payload is a one-shot answer to one challenge; after a refused attempt
  // the player needs a fresh one, and — since verification now waits for a
  // real click on the checkbox rather than firing itself — a bare `reset()`
  // is the whole story: it drops the stale payload and puts the checkbox back
  // in the unverified state, which is exactly what a second click needs to
  // find.
  if (!ok) {
    altchaPayload.value = '';
    altchaEl.value?.reset?.();
  }
}

// ─── Remind password ──────────────────────────────────────────────────────
// A separate, smaller gate: just an email and a solved captcha, calling
// core's own `forgotPassword` directly through `net/api.ts` — the same
// precedent `ResetPasswordScreen.vue` sets by calling `api.resetPassword`
// directly rather than through `state/session.ts`, since neither call
// changes who is signed in.

const forgotEmail = ref('');
const forgotAltchaPayload = ref('');
const forgotAltchaEl = ref<(HTMLElement & { reset?: () => void; verify?: () => void }) | null>(
  null,
);
const forgotBusy = ref(false);
const forgotDone = ref(false);
const forgotError = ref<string | null>(null);

const canSubmitForgot = computed(
  () => !forgotBusy.value && forgotEmail.value.trim().length > 0 && forgotAltchaPayload.value !== '',
);

function onForgotAltchaState(event: Event): void {
  const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
  forgotAltchaPayload.value = detail?.state === 'verified' ? (detail.payload ?? '') : '';
}

async function submitForgot(): Promise<void> {
  if (!canSubmitForgot.value) return;
  forgotBusy.value = true;
  forgotError.value = null;
  try {
    await api.forgotPassword(forgotEmail.value.trim(), forgotAltchaPayload.value);
    forgotDone.value = true;
  } catch (err) {
    forgotError.value =
      err instanceof ApiError
        ? err.message
        : 'Could not reach the server. Try again in a moment.';
    forgotAltchaPayload.value = '';
    forgotAltchaEl.value?.reset?.();
  } finally {
    forgotBusy.value = false;
  }
}

function goToForgot(): void {
  mode.value = 'forgot';
  forgotDone.value = false;
  forgotError.value = null;
}
</script>

<template>
  <div class="landing screen" :style="{ backgroundImage: `url(${BACKDROP.image})` }">
    <video
      v-if="!backdropReducedMotion"
      class="backdrop-video"
      :class="{ ready: backdropVideoReady }"
      autoplay
      muted
      loop
      playsinline
      preload="auto"
      @playing="backdropVideoReady = true"
    >
      <source :src="BACKDROP.webm" type="video/webm" />
      <source :src="BACKDROP.mp4" type="video/mp4" />
    </video>
    <div class="scrim" aria-hidden="true"></div>

    <div class="panel" :class="{ locked: isSubmitting }">
      <span class="bracket bracket-tl" aria-hidden="true"></span>
      <span class="bracket bracket-tr" aria-hidden="true"></span>
      <span class="bracket bracket-bl" aria-hidden="true"></span>
      <span class="bracket bracket-br" aria-hidden="true"></span>

      <button type="button" class="close" data-test="auth-back" aria-label="Back to the briefing" @click="emit('back')">
        &times;
      </button>

      <BrandLogo class="panel-logo" aria-hidden="true" />
      <span class="sr-only">Steel of War</span>

      <div v-if="!isForgot" class="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          data-test="tab-login"
          :aria-selected="!isRegister"
          :class="{ active: !isRegister }"
          @click="mode = 'login'"
        >
          Sign in
        </button>
        <button
          type="button"
          role="tab"
          data-test="tab-register"
          :aria-selected="isRegister"
          :class="{ active: isRegister }"
          @click="mode = 'register'"
        >
          Register
        </button>
      </div>

      <template v-if="!isForgot">
        <form :class="{ scroll: isRegister }" @submit.prevent="submit">
          <div class="scroll-body">
            <template v-if="isRegister">
              <label for="nickname">Call sign</label>
              <input
                id="nickname"
                v-model="nickname"
                maxlength="16"
                autocomplete="username"
                pattern="[A-Za-z0-9_\-]+"
                placeholder="3-16 characters"
                :disabled="session.busy.value"
              />
            </template>

            <label for="email">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              autocomplete="email"
              :placeholder="isRegister ? 'Signs you in, and recovers the account' : ''"
              :disabled="session.busy.value"
            />

            <PasswordField
              id="password"
              v-model="password"
              label="Password"
              :autocomplete="isRegister ? 'new-password' : 'current-password'"
              :placeholder="isRegister ? 'At least 8 characters' : ''"
              :disabled="session.busy.value"
              data-test="password"
            />

            <template v-if="isRegister">
              <PasswordField
                id="repeat-password"
                v-model="repeatPassword"
                label="Repeat password"
                autocomplete="new-password"
                :disabled="session.busy.value"
                data-test="repeat-password"
              />

              <div class="checkbox-row">
                <input
                  id="agree-terms"
                  type="checkbox"
                  v-model="agreedToTerms"
                  :disabled="session.busy.value"
                  data-test="agree-terms"
                  aria-label="I agree to the terms of service and the privacy policy"
                />
                <span class="checkbox-text">
                  I agree to the
                  <button type="button" class="inline-link" @click="legalSlug = 'terms-of-service'">
                    terms of service
                  </button>
                  and the
                  <button type="button" class="inline-link" @click="legalSlug = 'privacy-policy'">
                    privacy policy
                  </button>.
                </span>
              </div>
            </template>

            <template v-else>
              <button type="button" class="forgot-link" @click="goToForgot">
                Forgot your password?
              </button>
            </template>

            <altcha-widget
              ref="altchaEl"
              class="altcha"
              challenge="/api/auth/altcha-challenge"
              name="altcha"
              :configuration="ALTCHA_CONFIG"
              data-test="altcha"
              @statechange="onAltchaState"
            ></altcha-widget>
          </div>

          <button
            type="submit"
            class="submit"
            :class="{ live: canSubmit }"
            data-test="submit"
            :disabled="!canSubmit"
          >
            {{
              session.busy.value
                ? 'Working…'
                : isRegister
                  ? 'Register and enter the garage'
                  : 'Sign in'
            }}
          </button>
        </form>

        <p v-if="session.error.value" class="error" data-test="auth-error">
          {{ session.error.value }}
        </p>
      </template>

      <template v-else>
        <h2>Remind password</h2>

        <template v-if="forgotDone">
          <p class="tagline">
            If that email exists, a reset link is on its way. Check your inbox.
          </p>
          <button type="button" class="submit live" @click="mode = 'login'">Back to sign in</button>
        </template>

        <template v-else>
          <form @submit.prevent="submitForgot">
            <label for="forgot-email">Email</label>
            <input
              id="forgot-email"
              v-model="forgotEmail"
              type="email"
              autocomplete="email"
              :disabled="forgotBusy"
            />

            <altcha-widget
              ref="forgotAltchaEl"
              class="altcha"
              challenge="/api/auth/altcha-challenge"
              name="altcha"
              :configuration="ALTCHA_CONFIG"
              @statechange="onForgotAltchaState"
            ></altcha-widget>

            <button
              type="submit"
              class="submit"
              :class="{ live: canSubmitForgot }"
              :disabled="!canSubmitForgot"
            >
              {{ forgotBusy ? 'Working…' : 'Send reset link' }}
            </button>
          </form>

          <p v-if="forgotError" class="error">{{ forgotError }}</p>

          <button type="button" class="forgot-link" @click="mode = 'login'">
            &larr; Back to sign in
          </button>
        </template>
      </template>
    </div>

    <LegalOverlay :slug="legalSlug" @close="legalSlug = null" />
  </div>
</template>

<style src="../landing/landing.css"></style>

<style scoped>
/* The panel floats left rather than centering — the backdrop is shot with
   the tank held right-of-centre and a dark, empty doorway on the left, so
   the card sits in the negative space the photograph already leaves for it
   instead of covering the one thing in frame. */
.screen {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 2rem var(--gutter);
  overflow-y: auto;
  background-color: var(--ink);
  background-size: cover;
  background-position: center;
}

/* Layered over the still, under the scrim — same stacking `LandingScreen.vue`'s
   "Ground" section uses for its own video-over-image. `object-position`
   matches the still's own `background-position: center` so the fade between
   the two never jumps. */
.backdrop-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transition: opacity 1.1s ease;
}

.backdrop-video.ready {
  opacity: 1;
}

/* A centre-heavy vignette rather than the loading screen's bottom-heavy one —
   that screen puts text over the bottom-left of its painting; this one puts
   an opaque card in the middle of it, so what needs taming is everything
   *around* the card, evenly. A faint warm glow behind the panel ties the
   accent colour to the one thing on screen worth looking at twice. */
.scrim {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse 60% 50% at 50% 42%,
      color-mix(in srgb, var(--amber) 16%, transparent) 0%,
      transparent 60%
    ),
    radial-gradient(ellipse 90% 80% at 50% 50%, transparent 0%, color-mix(in srgb, var(--ink) 88%, transparent) 78%),
    color-mix(in srgb, var(--ink) 42%, transparent);
}

/* `max-height: 100%` rather than a hard-coded viewport figure — a flex
   item's percentage height resolves against `.screen`'s own content box, so
   this is already clear of the 2rem padding `.screen` carries on every edge:
   the same margin the rest of the screen keeps, not a second number to keep
   in step with it. `.screen`'s own `overflow-y: auto` is a defensive
   fallback for an edge case even the header row cannot fit; ordinarily
   nothing overflows it any more; the register form is what scrolls (see
   `form.scroll`, below), not the panel or the screen. */
.panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  /* A flex item's automatic minimum height is its content's own, and that
     wins over an explicit `max-height` when the two conflict — so without
     this, the panel's own children (logo, tabs, the whole form) could force
     it past `max-height` regardless of the cap, on exactly the short
     viewport this is meant to protect. */
  min-height: 0;
  width: min(440px, calc(100vw - 2 * var(--gutter)));
  max-height: 100%;
  padding: 2.25rem;
  border: 1px solid var(--zinc);
  border-radius: 2px;
  background: color-mix(in srgb, var(--steel) 88%, transparent);
  backdrop-filter: blur(6px);
  box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.65);
}

/* Gunsight corners rather than a full frame — a border already draws the
   panel's own edge, and four brackets read as a targeting reticle in a way
   a rectangle cannot. Each is a plain L: two sides of a square with the
   other two left transparent, held clear of the panel's own border so it
   reads as bracketing the card from a distance rather than tracing it. */
.bracket {
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  border-style: solid;
  border-width: 0;
  border-color: color-mix(in srgb, var(--frost) 80%, transparent);
  opacity: 0.7;
  pointer-events: none;
  transition: border-color 0.5s ease, opacity 0.5s ease;
}

.bracket-tl {
  top: -24px;
  left: -24px;
  border-top-width: 1.5px;
  border-left-width: 1.5px;
}

.bracket-tr {
  top: -24px;
  right: -24px;
  border-top-width: 1.5px;
  border-right-width: 1.5px;
}

.bracket-bl {
  bottom: -24px;
  left: -24px;
  border-bottom-width: 1.5px;
  border-left-width: 1.5px;
}

.bracket-br {
  bottom: -24px;
  right: -24px;
  border-bottom-width: 1.5px;
  border-right-width: 1.5px;
}

/* The small easter egg: a request in flight reads as locked-on, in the exact
   green the in-game HUD already uses for a friendly nameplate (`#4FE06A`,
   see `CLAUDE.game.md`'s note on team colour) — the one colour on this
   screen that means something specific elsewhere in the game rather than
   being picked for this screen alone. Reverts the instant `busy` does; see
   `isSubmitting`'s own comment for why that is the right moment and not a
   fixed delay. */
.panel.locked .bracket {
  border-color: #4fe06a;
  opacity: 1;
}

/* Top-right corner of the card rather than a line in the flow — the arrow
   read as a step in the form, and it was one of the things pushing the
   register form past the viewport. A plain cross in the corner is the
   ordinary place a dismissal control sits and costs the layout nothing. */
.close {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  min-width: 44px;
  min-height: 44px;
  margin: -0.6rem -0.6rem 0 0;
  border: none;
  border-radius: 3px;
  background: none;
  color: var(--frost);
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.close:hover {
  color: var(--bone);
  background: color-mix(in srgb, var(--zinc) 60%, transparent);
}

.panel-logo {
  display: block;
  --brand-logo-height: 2rem;
  margin-bottom: 0.5rem;
}

.tagline {
  margin: 0 0 1.5rem;
  font-family: var(--data);
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--amber);
}

/* `.panel h2` rather than a bare `h2` — `landing.css`'s own `.landing h2` is
   sized for a hero heading (up to 2.9rem) and ties this rule's specificity
   exactly, so the extra class is what decides it rather than source order. */
.panel h2 {
  margin: 0 0 0.5rem;
  font-family: var(--display);
  font-variation-settings: 'wdth' 108, 'wght' 700;
  font-size: 1.3rem;
  letter-spacing: 0.04em;
  color: var(--bone);
}

.tabs {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid var(--zinc);
}

.tabs button {
  flex: 1;
  padding: 0.6rem 0.5rem;
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  font-family: var(--data);
  color: var(--frost);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
}

.tabs button.active {
  color: var(--bone);
  border-bottom-color: var(--amber);
}

/* A flex column rather than the grid itself — `.scroll-body` carries the
   grid now, so the submit button can sit beside it as an ordinary flex
   child rather than as one more row inside the thing that scrolls. */
/* `min-height: 0` is load-bearing, not tidy-up. A flex item's automatic
   minimum size is its content's natural height unless this overrides it —
   without it, `form` refuses to shrink below fitting `.scroll-body` at full
   height, so the shrink signal never reaches `.scroll-body`'s own
   `overflow-y: auto` and `.panel`'s `max-height` cap just gets exceeded,
   visibly, with nothing scrolling. That is the exact "no scroll" failure on
   a short viewport. */
form {
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 0.4rem;
}

.scroll-body {
  display: grid;
  gap: 0.1rem;
}

/* Register-only: with the call sign, both password fields and the terms row
   on top of email and the check, this is the one body tall enough to run
   past a short viewport. `.panel`'s own `max-height: 100%` stops the *card*
   from growing past the screen; `min-height: 0` is what actually lets a flex
   item shrink below its content size, which is what makes the
   `overflow-y: auto` below take effect instead of the grid simply pushing
   the panel taller regardless. The submit button sits *outside* this body,
   as `form`'s own next flex child, so it never scrolls out of reach — the
   scroll is for the fields, not for the one control that matters most.
   Login and the reset-link form stay short enough that they never need
   this. */
form.scroll .scroll-body {
  min-height: 0;
  padding-right: 0.4rem;
  margin-right: -0.4rem;
  overflow-y: auto;
}

label {
  margin-top: 0.35rem;
  font-family: var(--data);
  font-size: 0.65rem;
  line-height: 1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--frost);
}

input {
  padding: 0.4rem 0.85rem;
  border: 1px solid var(--zinc);
  border-radius: 3px;
  background: var(--ink);
  color: var(--bone);
  font: inherit;
}

input:focus {
  outline: none;
  border-color: var(--amber);
}

/* Dimmer than the typed value, deliberately — a placeholder at the same
   contrast as real text reads as an answer already filled in rather than
   an example of one. */
input::placeholder {
  color: color-mix(in srgb, var(--frost) 65%, transparent);
}

/* Not a `<label>` — see the template's own note. The row is not itself a
   click target; only the checkbox and the two document links are. */
.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 0.6rem;
}

.checkbox-text {
  font-family: var(--body);
  font-size: 0.8rem;
  line-height: 1.4;
  letter-spacing: normal;
  text-transform: none;
  color: var(--frost);
}

.inline-link {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: var(--bone);
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--amber) 70%, transparent);
  text-underline-offset: 0.15em;
  cursor: pointer;
}

.inline-link:hover {
  color: var(--amber);
}

/* Sized to the label text (0.8rem) rather than to the 44px touch-target
   floor — a checkbox rendered at 44px reads as oversized next to an 0.8rem
   line and there is no `::before` trick available on a replaced element like
   `input[type=checkbox]` to grow the hit area without growing the box. The
   floor is a real rule elsewhere in this codebase; this is the stated
   exception, traded for matching the text it sits beside. */
.checkbox-row input {
  flex: none;
  width: 1rem;
  height: 1rem;
  margin: 0.15rem 0 0;
  accent-color: var(--amber);
  cursor: pointer;
}

/* The generic `input:focus` rule strips the outline for every input and
   relies on `border-color` to show focus instead — fine for a bordered text
   field, invisible on a 1rem checkbox drawn mostly by `accent-color`. This
   reinstates a real ring for keyboard navigation specifically here, rather
   than loosening the text-field rule to fix a problem only the checkbox has. */
.checkbox-row input:focus-visible {
  outline: 2px solid var(--amber);
  outline-offset: 2px;
}

.forgot-link {
  justify-self: start;
  padding: 0;
  margin-top: 0.2rem;
  border: none;
  background: none;
  font-family: var(--data);
  font-size: 0.76rem;
  letter-spacing: 0.04em;
  color: var(--frost);
  cursor: pointer;
}

.forgot-link:hover {
  color: var(--bone);
}

/* The widget is `display: none` until it decides it is ready to render (its
   own stylesheet gates on a `[data-visible]` attribute) — forcing `block`
   here is what makes it show up rather than a styling nicety. Its actual
   custom-property names are the library's own (`--altcha-border-color`, not
   `--altcha-color-border`) — two of the four names below were previously
   invented and had no effect at all, so the widget rendered in its unstyled
   default until this pass. */
.altcha {
  display: block;
  min-height: 2.6rem;
  margin-top: 0.75rem;
  font-family: var(--data) !important;
  font-size: 0.8rem !important;

  --altcha-max-width: 100%;
  --altcha-padding: 0.55rem 0.7rem;
  --altcha-border-width: 1px;
  --altcha-border-radius: 3px;
  --altcha-border-color: var(--zinc);
  /* Transparent rather than a boxed colour — the panel's own translucent,
     blurred background shows through, so the check reads as part of the
     page rather than as a separate control sitting on top of it. */
  --altcha-color-base: transparent;
  --altcha-color-base-content: var(--bone);
  --altcha-color-neutral: var(--zinc);
  --altcha-color-neutral-content: var(--frost);
  --altcha-color-primary: var(--amber);
  --altcha-color-primary-content: #12100c;
  /* The checked state reuses the gunsight brackets' "locked on" green — the
     same colour the panel's corners turn to while a request is in flight, so
     a solved check and a submitting form read as the same idea. */
  --altcha-color-success: #4fe06a;
  --altcha-color-success-content: #12100c;
  --altcha-color-error: #d9483b;
  --altcha-color-error-content: var(--bone);
  --altcha-checkbox-size: 18px;
  --altcha-checkbox-border-color: var(--zinc);
  --altcha-checkbox-border-radius: 3px;
  --altcha-spinner-color: var(--amber);
  --altcha-transition-duration: 0.4s;
}

/* `<altcha-widget>` renders into light DOM (its base stylesheet is injected
   by its own script, but nothing here attaches a shadow root), so — unlike
   a genuinely shadow-encapsulated element — its internal classes really are
   reachable through `:deep()`. The logo has no size variable of its own; its
   SVG carries a fixed 22x22 `width`/`height` attribute, which an ordinary CSS
   rule outranks. */
.altcha :deep(.altcha-logo svg) {
  width: 14px;
  height: 14px;
}

/* Belt and braces on the font: the host-level `.altcha { font-family: ... }`
   rule above relies on the widget's own stylesheet ending in
   `font-family: inherit` after its `all: revert-layer` reset, which should
   carry it down — but that is still one hop of inheritance depending on a
   third-party file behaving exactly as read. This sets it directly on every
   descendant instead, so it cannot depend on that chain at all. */
.altcha :deep(*) {
  font-family: var(--data) !important;
}

/* Disabled until the form actually is one — see `join-gate.ts`. The instant
   it comes alive it turns the accent colour and starts a slow diagonal
   sweep, so "you can press this now" is felt before it is read. */
.submit {
  position: relative;
  overflow: hidden;
  margin-top: 1rem;
  padding: 0.75rem;
  border: none;
  border-radius: 3px;
  background: var(--zinc);
  color: var(--frost);
  font-family: var(--display);
  font-variation-settings: 'wdth' 106, 'wght' 700;
  font-size: 0.86rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: not-allowed;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.submit.live {
  background: var(--amber);
  color: #12100c;
  cursor: pointer;
}

.submit:disabled {
  cursor: not-allowed;
}

.submit.live::after {
  content: '';
  position: absolute;
  top: 0;
  left: -45%;
  width: 45%;
  height: 100%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: submit-glare 2.6s ease-in-out infinite;
  pointer-events: none;
}

@keyframes submit-glare {
  from {
    transform: translateX(0%);
  }
  to {
    transform: translateX(320%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .submit.live::after {
    animation: none;
    opacity: 0;
  }
}

.error {
  margin: 1rem 0 0;
  padding: 0.6rem 0.75rem;
  border-left: 2px solid #c0392b;
  background: rgba(192, 57, 43, 0.12);
  font-size: 0.85rem;
  color: var(--bone);
}

/*
 * A phone in landscape: short, not merely narrow — the same `(max-height:
 * 520px)` breakpoint `SettingsPanel.vue`, `BattlePicker.vue` and
 * `RankLadder.vue` already key their own compact passes off. This screen
 * never had one: `RotatePrompt` used to force landscape everywhere, including
 * here, so nobody had ever actually seen this form at a real phone's
 * landscape height. At 667x375 the *login* form alone — logo, tabs, email,
 * password, the forgot-password link, the ALTCHA widget's own fixed
 * `min-height`, submit — already exceeds the ~311px `.panel` has to work
 * with at the desktop spacing above, well before register's extra fields are
 * even in the picture. Every rule below is the same content shrunk, not a
 * different layout: the desktop rhythm two-and-a-quarter-rem-and-up assumes a
 * landscape hero photo's worth of height, and a handheld's short edge simply
 * does not have it to spend.
 */
@media (max-height: 520px) {
  .screen {
    padding: 0.75rem var(--gutter);
  }

  .panel {
    padding: 1rem 1.25rem;
  }

  .panel-logo {
    --brand-logo-height: 1.4rem;
    margin-bottom: 0.2rem;
  }

  .tabs {
    margin-bottom: 0.5rem;
  }

  .tabs button {
    padding: 0.4rem 0.5rem;
  }

  form {
    gap: 0.2rem;
  }

  label {
    margin-top: 0.15rem;
    font-size: 0.6rem;
    line-height: 0.85rem;
  }

  input {
    padding: 0.3rem 0.7rem;
    min-height: 2.5rem;
  }

  .checkbox-row {
    margin-top: 0.3rem;
  }

  .altcha {
    margin-top: 0.4rem;
  }

  .submit {
    margin-top: 0.5rem;
    padding: 0.55rem;
    min-height: 2.75rem;
  }

  /*
   * `form.scroll .scroll-body` (below the tabs, in the main rule set) only
   * ever engaged for `isRegister` — the desktop assumption was that *login*
   * never grows tall enough to need it. That assumption is exactly what a
   * short phone breaks, and the arithmetic above says login can overflow
   * here too even after every reduction above. Applied to every form at this
   * height rather than only `.scroll`, so login gets the same scroll safety
   * net register already has: harmless if the shrink above is enough on its
   * own, load-bearing the moment a real device's font metrics or the
   * ALTCHA widget's own chrome make it not quite enough.
   */
  .scroll-body {
    min-height: 0;
    padding-right: 0.4rem;
    margin-right: -0.4rem;
    overflow-y: auto;
  }
}
</style>
